import {
  doc,
  getDoc,
  Timestamp,
  increment,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  ADMIN_PROMO_COLLECTION,
  COLLECTIONS,
  DATA_ENVIRONMENT,
} from '../firebase/collections';

const PROMO_PATTERN = /^[A-Z0-9_-]{3,64}$/;

const ERROR_MESSAGES = {
  'functions/invalid-argument': 'Enter a valid promo code.',
  'functions/not-found': 'This promo code is invalid.',
  'functions/already-exists':
    'A promo code has already been redeemed on this account.',
  'functions/resource-exhausted':
    'This promo code has reached its redemption limit.',
  'functions/unauthenticated':
    'You must be signed in to redeem a promo code.',
  'functions/unavailable':
    'Promo code validation is temporarily unavailable. Please try again.',
};

export function normalizePromoCode(value) {
  return String(value ?? '')
    .replace(/\s/g, '')
    .toUpperCase();
}

export function validatePromoCode(value) {
  const normalizedCode = normalizePromoCode(value);

  if (!normalizedCode) {
    return null;
  }

  if (!PROMO_PATTERN.test(normalizedCode)) {
    return 'Use 3-64 letters, numbers, hyphens or underscores.';
  }

  return null;
}

export function resolvePromoDuration(data = {}) {
  if (data.lifetime === true || data.duration?.unit === 'lifetime') {
    return { value: 1, unit: 'lifetime' };
  }

  if (
    data.unit &&
    data.value != null &&
    data.duration == null &&
    data.durationDays == null
  ) {
    return {
      value: Number(data.value) || 0,
      unit: data.unit,
    };
  }

  if (data.duration && typeof data.duration === 'object') {
    return {
      value: Number(data.duration.value) || 0,
      unit: data.duration.unit || 'days',
    };
  }

  if (data.durationValue != null) {
    return {
      value: Number(data.durationValue) || 0,
      unit: data.durationUnit || 'days',
    };
  }

  if (typeof data.durationDays === 'number') {
    return { value: data.durationDays, unit: 'days' };
  }

  if (typeof data.duration === 'number') {
    return { value: data.duration, unit: 'days' };
  }

  return { value: 0, unit: 'days' };
}

export function formatPromoDuration(duration) {
  const { value, unit } = resolvePromoDuration(
    duration?.unit ? { duration } : duration
  );

  if (unit === 'lifetime') return 'Lifetime';
  if (!value) return '—';
  return `${value} ${unit}`;
}

export function calculatePromoExpiresAt(duration, fromDate = new Date()) {
  const resolved = resolvePromoDuration(
    duration?.unit ? { duration } : duration
  );

  if (!resolved.value || resolved.unit === 'lifetime') {
    return null;
  }

  const expires = new Date(fromDate);

  switch (resolved.unit) {
    case 'days':
      expires.setDate(expires.getDate() + resolved.value);
      break;
    case 'weeks':
      expires.setDate(expires.getDate() + resolved.value * 7);
      break;
    case 'months':
      expires.setMonth(expires.getMonth() + resolved.value);
      break;
    case 'years':
      expires.setFullYear(expires.getFullYear() + resolved.value);
      break;
    default:
      expires.setDate(expires.getDate() + resolved.value);
      break;
  }

  return expires;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSubscriptionActivePremium(sub = {}) {
  if (sub.plan !== 'premium' || sub.status !== 'active') return false;
  if (sub.isActive === false) return false;

  const expiresAt = toDate(sub.expiresAt);
  return !expiresAt || expiresAt > new Date();
}

function isPaidActivePremium(sub = {}) {
  if (!isSubscriptionActivePremium(sub)) return false;
  const source = String(sub.source || '').toLowerCase();
  if (source === 'promo') return false;
  // Paid / store / unknown active premium blocks promo redemption.
  return true;
}

function getServerMessage(error) {
  const details = error?.details;

  if (typeof details === 'string') return details;
  if (typeof details?.message === 'string') return details.message;
  return null;
}

/**
 * Redeem via Cloud Function for the currently authenticated user (mobile/web signup flow).
 */
export async function redeemPromoCode(
  code,
  dataEnvironment = DATA_ENVIRONMENT
) {
  const normalizedCode = normalizePromoCode(code);

  if (!normalizedCode) {
    return null;
  }

  const validationError = validatePromoCode(normalizedCode);
  if (validationError) {
    throw new Error(validationError);
  }

  try {
    const redeem = httpsCallable(functions, 'redeem_promo_code');
    const result = await redeem({
      code: normalizedCode,
      dataEnvironment,
    });
    return result.data;
  } catch (error) {
    const message =
      getServerMessage(error) ||
      ERROR_MESSAGES[error?.code] ||
      'Could not redeem this promo code. Please try again.';

    const mappedError = new Error(message);
    mappedError.code = error?.code;
    mappedError.isPromoCodeError = true;
    mappedError.original = error;
    throw mappedError;
  }
}

/**
 * Admin apply: mirror Cloud Function writes for a target user UID.
 * Uses the admin-writable promo collection. promoRedemptions is blocked by
 * current rules, so one-time redemption is tracked on the user document.
 */
export async function applyPromoCodeToUser({
  promoDocumentId,
  uid,
  collectionName = ADMIN_PROMO_COLLECTION,
}) {
  if (!promoDocumentId || !uid) {
    throw new Error('Promo code and user are required.');
  }

  const promoRef = doc(db, collectionName, String(promoDocumentId));
  const userRef = doc(db, COLLECTIONS.USERS, String(uid));
  const currentSubRef = doc(
    db,
    COLLECTIONS.USERS,
    String(uid),
    'subscription',
    'current'
  );
  const legacySubRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, String(uid));

  return runTransaction(db, async (transaction) => {
    const [promoSnap, userSnap, currentSubSnap] = await Promise.all([
      transaction.get(promoRef),
      transaction.get(userRef),
      transaction.get(currentSubRef),
    ]);

    if (!promoSnap.exists()) {
      throw new Error('This promo code is invalid.');
    }

    if (!userSnap.exists()) {
      throw new Error('User profile was not found.');
    }

    const userData = userSnap.data() || {};
    if (userData.promoCodeId || userData.promoRedeemedAt) {
      throw new Error('A promo code has already been redeemed on this account.');
    }

    const promo = promoSnap.data() || {};
    const status = promo.status || (promo.isActive === false ? 'inactive' : 'active');

    if (status !== 'active') {
      throw new Error('This promo code is inactive.');
    }

    const validFrom = toDate(promo.validFrom);
    if (validFrom && validFrom > new Date()) {
      throw new Error('This promo code is not active yet.');
    }

    const redemptionDeadline = toDate(promo.expiryDate || promo.expiresAt);
    if (redemptionDeadline && redemptionDeadline < new Date()) {
      throw new Error('This promo code has expired.');
    }

    const usedCount =
      Number(promo.usedCount ?? promo.redemptionCount ?? promo.used_count ?? 0) || 0;
    const maxUses = promo.maxUses ?? promo.max_uses;
    if (maxUses != null && Number(maxUses) > 0 && usedCount >= Number(maxUses)) {
      throw new Error('This promo code has reached its redemption limit.');
    }

    const duration = resolvePromoDuration(promo);
    if (!duration.value && duration.unit !== 'lifetime') {
      throw new Error('This promo code has an invalid duration.');
    }

    const currentSub = currentSubSnap.exists() ? currentSubSnap.data() : {};
    if (isPaidActivePremium(currentSub)) {
      throw new Error(
        'This user already has an active paid Premium subscription.'
      );
    }

    const startedAt = new Date();
    const expiresAtDate = calculatePromoExpiresAt(duration, startedAt);
    const expiresAtTs = expiresAtDate ? Timestamp.fromDate(expiresAtDate) : null;
    const now = serverTimestamp();
    const promoCode = normalizePromoCode(promo.code || promoDocumentId);

    transaction.set(
      currentSubRef,
      {
        plan: 'premium',
        status: 'active',
        isActive: true,
        isPremium: true,
        source: 'promo',
        promoCodeId: String(promoDocumentId),
        productId: null,
        billingPeriod: 'promotional',
        startedAt: Timestamp.fromDate(startedAt),
        expiresAt: expiresAtTs,
        willRenew: false,
        updatedAt: now,
      },
      { merge: true }
    );

    transaction.set(
      userRef,
      {
        isPremium: true,
        subscriptionExpirationDate: expiresAtTs,
        subscriptionUpdatedAt: now,
        promoCodeId: String(promoDocumentId),
        promoCodeUsed: promoCode,
        promoRedeemedAt: now,
        updatedAt: now,
      },
      { merge: true }
    );

    transaction.set(
      legacySubRef,
      {
        isActive: true,
        planId: 'promo',
        expiresAt: expiresAtTs,
        updatedAt: now,
      },
      { merge: true }
    );

    const promoUpdate = {
      usedCount: increment(1),
      updatedAt: now,
    };
    if (promo.redemptionCount != null && promo.usedCount == null) {
      promoUpdate.redemptionCount = increment(1);
    }

    transaction.update(promoRef, promoUpdate);

    return {
      success: true,
      plan: 'premium',
      isLifetime: expiresAtTs === null,
      expiresAt: expiresAtDate ? expiresAtDate.toISOString() : null,
      promoCode,
    };
  });
}

export async function getUserCurrentSubscription(uid) {
  const snap = await getDoc(
    doc(db, COLLECTIONS.USERS, String(uid), 'subscription', 'current')
  );
  return snap.exists() ? snap.data() : null;
}
